import { ApexBarChart } from '@/components'
import { useAxios } from '@/context/axiosContext'
import {
  generatePeriod,
  useGolfSearchData,
  mapWeekDataToGraph,
} from '@/pages/main/_components/golfSearch/useGolfSearchData'
import {
  GraphDataState,
  WeekAvgPriceByRegionItem,
} from '@/redux/slices/golfSearchSlice'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { RootState } from '@/redux/store'
import { useEffect, useState, useRef, useCallback, useMemo } from 'react'
import { useRedis } from '@/hooks/useRedis'
import { useResponsive } from '@/hooks/useResponsive'

interface WeekGraphProps {
  weekData: GraphDataState['weekData']
  weekAvgPriceByRegion?: GraphDataState['weekAvgPriceByRegion']
  isWeekDualGraph?: boolean
  areaCd?: string | null
  areaNm?: string
  bizCd?: string
  bizNm?: string
  detailAreaNm?: string
  mshipYn?: string
  holeType?: string
}

interface BizListItem {
  areaCd: string | null
  areaNm: string | null
  year: string
  week: string | null
  quarter: string | null
  n01: number | null
  n02: number | null
  m01: number | null
  m02: number | null
}

export const WeekGraph = ({
  weekData,
  weekAvgPriceByRegion,
  isWeekDualGraph,
  areaCd,
  areaNm,
  bizCd,
  bizNm,
  detailAreaNm,
  mshipYn = '',
  holeType = '18',
}: WeekGraphProps) => {
  const safeWeekGraphData = weekData?.length ? weekData : []
  const safeWeekRegionGraphData = weekAvgPriceByRegion?.length
    ? weekAvgPriceByRegion
    : []

  // 상태 선언 - 초기값을 명확히 설정
  const [nationalWeekData, setNationalWeekData] = useState<
    GraphDataState['weekData']
  >([])
  const [isNationalDataLoading, setIsNationalDataLoading] = useState(false)
  const [avgList, setAvgList] = useState<BizListItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false) // 초기화 상태 추가
  const { isMobile, isTablet, isDesktop } = useResponsive()
  // 차트 리프레시를 위한 상태 추가
  const [chartRefreshKey, setChartRefreshKey] = useState(0)
  const chartRefs = useRef<{ [key: string]: any }>({})
  const refreshExecutedRef = useRef(false) // 리프레시 실행 여부 추적

  // 전국 데이터 안전 처리
  const safeNationalWeekData = nationalWeekData?.length ? nationalWeekData : []

  // 데이터 유효성 검사 최적화
  const isWeekNonMemberDataEmpty = useMemo(
    () => safeNationalWeekData[0]?.every((item) => item === 0) ?? true,
    [safeNationalWeekData],
  )

  const isWeekMemberDataEmpty = useMemo(
    () => safeNationalWeekData[2]?.every((item) => item === 0) ?? true,
    [safeNationalWeekData],
  )

  const selectGolf = useSelector(
    (state: RootState) => state.golfSearch.selectMapData,
  )
  const axios = useAxios()

  // 효과적인 값들 계산 - 메모화
  const effectiveBizCd = useMemo(
    () => bizCd || selectGolf.bizCd,
    [bizCd, selectGolf.bizCd],
  )
  const effectiveBizNm = useMemo(
    () => bizNm || selectGolf?.bizNm,
    [bizNm, selectGolf?.bizNm],
  )
  const effectiveAreaCd = useMemo(
    () => areaCd || selectGolf.areaCd,
    [areaCd, selectGolf.areaCd],
  )
  const effectiveHoleType = useMemo(
    () => holeType || selectGolf.holeType || '18',
    [holeType, selectGolf.holeType],
  )

  const { getComCodeValue } = useRedis()

  // 지역명 결정 로직 메모화
  const effectiveAreaNm = useMemo(() => {
    // 전국 선택이거나 지역 정보가 없는 경우 null 반환
    if (
      !effectiveAreaCd &&
      !areaNm &&
      !detailAreaNm &&
      !safeWeekRegionGraphData[0]?.areaNm
    ) {
      return null
    }

    // 1. 직접 전달받은 areaNm이 있으면 사용
    if (areaNm) return areaNm

    // 2. detailAreaNm이 있으면 사용
    if (detailAreaNm) return detailAreaNm

    // 3. 지역 데이터에서 areaNm 확인
    if (safeWeekRegionGraphData[0]?.areaNm)
      return safeWeekRegionGraphData[0].areaNm

    // 4. areaCd로 코드명 조회 (areaCd가 유효한 경우만)
    if (effectiveAreaCd && effectiveAreaCd !== '' && effectiveAreaCd !== null) {
      const codeInfo = getComCodeValue('AREA_CD', effectiveAreaCd)
      if (codeInfo?.codeNm) return codeInfo.codeNm
    }

    return null
  }, [
    areaNm,
    detailAreaNm,
    effectiveAreaCd,
    safeWeekRegionGraphData,
    getComCodeValue,
  ])

  // 이전 값들을 기억하기 위한 ref - 중복 호출 방지
  const previousDataRef = useRef<{
    areaCd: string
    holeType: string
    mshipYn: string
    bizCd: string
  }>({
    areaCd: '',
    holeType: '18',
    mshipYn: '',
    bizCd: '',
  })

  // 차트 강제 리프레시 함수 - 한 번만 실행되도록 최적화
  const forceRefreshCharts = useCallback(() => {
    if (refreshExecutedRef.current) return // 이미 실행된 경우 중단

    refreshExecutedRef.current = true

    // key를 변경하여 컴포넌트 전체를 다시 렌더링 (한 번만)
    setChartRefreshKey((prev) => prev + 1)

    // 3초 후 다시 실행 가능하도록 리셋 (새로운 데이터 로딩 시를 위해)
    setTimeout(() => {
      refreshExecutedRef.current = false
    }, 3000)
  }, [])

  // 차트 로딩 완료 후 리프레시 실행 - 제거 (중복 실행 방지)
  const handleChartReady = useCallback(
    (chartId: string, chartInstance: any) => {
      chartRefs.current[chartId] = chartInstance
      // 개별 차트별 리프레시 제거 - 전체 리프레시로 통합
    },
    [],
  )

  // 전국 데이터 fetch - 중복 호출 방지 및 최적화
  const fetchNationalData = useCallback(async () => {
    const currentKey = `${effectiveAreaCd}_${effectiveHoleType}_${mshipYn}`
    const previousKey = `${previousDataRef.current.areaCd}_${previousDataRef.current.holeType}_${previousDataRef.current.mshipYn}`

    // 이미 로딩 중이거나 같은 파라미터로 요청한 경우 중단
    if (isNationalDataLoading || currentKey === previousKey) return

    try {
      setIsNationalDataLoading(true)

      const params = {
        holeType: effectiveHoleType,
        ...(mshipYn && { mshipYn }),
      }

      const response = await axios.get('/golf/weekStatWeekList', { params })

      if (response.data?.data?.weekAvgPriceData) {
        const { weekAvgPriceData } = response.data.data
        const weeksArr = generatePeriod('week')
        const updatedGraphData = mapWeekDataToGraph(weekAvgPriceData, weeksArr)
        setNationalWeekData(updatedGraphData)
      }

      // 이전 값 업데이트
      previousDataRef.current = {
        ...previousDataRef.current,
        areaCd: effectiveAreaCd || '',
        holeType: effectiveHoleType,
        mshipYn: mshipYn || '',
      }
    } catch (error) {
      console.error('Error fetching national week data:', error)
    } finally {
      setIsNationalDataLoading(false)
    }
  }, [
    effectiveAreaCd,
    effectiveHoleType,
    mshipYn,
    axios,
    isNationalDataLoading,
    forceRefreshCharts,
  ])

  // bizCd 데이터 fetch - 중복 호출 방지
  const fetchBizCdData = useCallback(async () => {
    if (!effectiveBizCd) {
      setAvgList([])
      return
    }

    const currentBizKey = `${effectiveBizCd}_${effectiveHoleType}`
    const previousBizKey = `${previousDataRef.current.bizCd}_${previousDataRef.current.holeType}`

    // 이미 로딩 중이거나 같은 파라미터로 요청한 경우 중단
    if (isLoading || currentBizKey === previousBizKey) return

    try {
      setIsLoading(true)

      const params = new URLSearchParams({
        bizCd: effectiveBizCd,
        ...(effectiveHoleType && { holeType: effectiveHoleType }),
      })

      const response = await axios.get(
        `/golf/bizCdAvgList?${params.toString()}`,
      )

      if (response.data) {
        const validData = response.data.data.bizListData.filter(
          (item: BizListItem) =>
            item.n01 !== null ||
            item.n02 !== null ||
            item.m01 !== null ||
            item.m02 !== null,
        )
        setAvgList(validData)
      }

      // 이전 값 업데이트
      previousDataRef.current = {
        ...previousDataRef.current,
        bizCd: effectiveBizCd,
        holeType: effectiveHoleType,
      }
    } catch (error) {
      console.error('Error fetching golf avg price:', error)
    } finally {
      setIsLoading(false)
    }
  }, [effectiveBizCd, effectiveHoleType, axios, isLoading, forceRefreshCharts])

  // 초기 데이터 설정 - 한 번만 실행
  useEffect(() => {
    if (!isInitialized) {
      // 지역 선택 여부와 관계없이 초기 데이터 설정
      if (safeWeekGraphData.length > 0) {
        setNationalWeekData(safeWeekGraphData)
      }
      setIsInitialized(true)
    }
  }, [isInitialized, safeWeekGraphData])

  // 전국 데이터 fetch - 지역 선택 시에만
  useEffect(() => {
    if (!isInitialized) return

    // 지역이 선택되어 있을 때만 별도 전국 데이터 fetch
    if (effectiveAreaCd && effectiveAreaCd !== '' && effectiveAreaCd !== null) {
      fetchNationalData()
    }
    // 전국 선택 시에는 기존 weekData 사용하되, 빈 상태면 다시 설정
    else if (
      safeWeekGraphData.length > 0 &&
      safeNationalWeekData.length === 0
    ) {
      setNationalWeekData(safeWeekGraphData)
    }
  }, [
    isInitialized,
    effectiveAreaCd,
    effectiveHoleType,
    mshipYn,
    fetchNationalData,
    safeWeekGraphData,
    safeNationalWeekData.length,
  ])

  // bizCd 데이터 fetch
  useEffect(() => {
    if (!isInitialized) return
    fetchBizCdData()
  }, [isInitialized, fetchBizCdData])

  // 모든 데이터 로딩 완료 후 차트 리프레시 - 한 번만 실행
  useEffect(() => {
    if (
      isInitialized &&
      !isLoading &&
      !isNationalDataLoading &&
      !refreshExecutedRef.current
    ) {
      // 모든 로딩이 완료된 후 한 번만 차트 리프레시
      const timer = setTimeout(() => {
        forceRefreshCharts()
      }, 500) // 약간 더 긴 지연으로 안정성 확보

      return () => clearTimeout(timer)
    }
  }, [isInitialized, isLoading, isNationalDataLoading, forceRefreshCharts])

  // Helper functions - 메모화
  const forwardFill = useCallback((arr: number[]): number[] => {
    let lastValid = 0
    return arr.map((val) => {
      if (val > 0) {
        lastValid = val
        return val
      }
      return lastValid
    })
  }, [])

  const weeksArr = useMemo(() => generatePeriod('week'), [])

  const mapAvgListToGraph = useCallback((): number[][] => {
    const rawData = avgList.reduce(
      (acc, item) => {
        if (!item.year || !item.week) return acc

        const weekString = item.week?.toString().padStart(2, '0') ?? '00'
        const weekIndex = weeksArr.findIndex(
          (w) => w === `${item.year}-${weekString}`,
        )

        if (weekIndex >= 0) {
          acc[0][weekIndex] = item.n01 || 0
          acc[1][weekIndex] = item.n02 || 0
          acc[2][weekIndex] = item.m01 || 0
          acc[3][weekIndex] = item.m02 || 0
        }

        return acc
      },
      Array.from({ length: 4 }, () => new Array(weeksArr.length).fill(0)),
    )

    return rawData.map((series) => forwardFill(series))
  }, [avgList, weeksArr, forwardFill])

  const mapWeekRegionAvgToGraph = useCallback(
    (regionData: WeekAvgPriceByRegionItem[] = []): number[][] => {
      const rawData = regionData.reduce(
        (acc, item) => {
          if (!item || !item.year || !item.week) return acc

          const weekString = item.week?.toString().padStart(2, '0') ?? '00'
          const weekIndex = weeksArr.findIndex(
            (w) => w === `${item.year}-${weekString}`,
          )

          if (weekIndex >= 0) {
            acc[0][weekIndex] = item.n01 || 0
            acc[1][weekIndex] = item.n02 || 0
            acc[2][weekIndex] = item.m01 || 0
            acc[3][weekIndex] = item.m02 || 0
          }
          return acc
        },
        Array.from({ length: 4 }, () => new Array(weeksArr.length).fill(0)),
      )

      return rawData.map((series) => forwardFill(series))
    },
    [weeksArr, forwardFill],
  )

  // 메모화된 그래프 데이터
  const enhancedWeekData = useMemo(
    () => mapAvgListToGraph(),
    [mapAvgListToGraph],
  )
  const regionWeekGraphData = useMemo(
    () => mapWeekRegionAvgToGraph(safeWeekRegionGraphData),
    [mapWeekRegionAvgToGraph, safeWeekRegionGraphData],
  )

  const getWeekStartDate = useCallback((weekStr: string): Date => {
    const [yearStr, weekStrNum] = weekStr.split('-')
    const year = parseInt(yearStr, 10)
    const week = parseInt(weekStrNum, 10)

    // 1월 1일 기준으로 시작 주의 월요일을 구함
    const jan1 = new Date(year, 0, 1)
    const jan1Day = jan1.getDay() // 일(0) ~ 토(6)
    const jan1Offset = (jan1Day + 6) % 7 // 월요일까지 며칠 전인지 계산
    const firstWeekMonday = new Date(jan1)
    firstWeekMonday.setDate(jan1.getDate() - jan1Offset)

    // 해당 주차의 시작 월요일
    const targetMonday = new Date(firstWeekMonday)
    targetMonday.setDate(firstWeekMonday.getDate() + (week - 1) * 7)

    return targetMonday
  }, [])

  // 공통 옵션 메모화 - 차트 이벤트 핸들러 제거 (중복 실행 방지)
  const commonOptions: ApexCharts.ApexOptions = useMemo(
    () => ({
      chart: {
        type: 'line',
        height: 350,
        toolbar: { show: false },
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 300,
          animateGradually: {
            enabled: false,
          },
          dynamicAnimation: {
            enabled: false,
          },
        },
        redrawOnParentResize: true,
        redrawOnWindowResize: true,
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        width: [0, 2, 5],
        curve: 'smooth',
      },
      markers: {
        size: [0, 4, 4], // 바 차트에는 마커 없음, 라인 차트에만 마커
        strokeWidth: 2,
        hover: {
          size: 6,
        },
      },
      tooltip: {
        shared: true,
        intersect: false,
        x: {
          formatter: (val: string | number) => {
            const weekStr =
              typeof val === 'number' ? weeksArr[val] : (val as string)
            const monday = getWeekStartDate(weekStr)
            const sunday = new Date(monday)
            sunday.setDate(sunday.getDate() + 6)
            return `${monday.getFullYear()}.${monday.getMonth() + 1}.${monday.getDate()} ~ ${sunday.getFullYear()}.${sunday.getMonth() + 1}.${sunday.getDate()}`
          },
        },
        y: {
          formatter: (val: number) => `${val.toLocaleString()}원`,
        },
      },
      legend: {
        show: true,
        position: 'bottom',
        horizontalAlign: 'center',
        showForSingleSeries: true,
      },
      xaxis: {
        categories: weeksArr,
        labels: {
          rotate: -45,
          rotateAlways: true,
          formatter: (value: string) => {
            const monday = getWeekStartDate(value)
            return `${monday.getMonth() + 1}/${monday.getDate()}~`
          },
        },
      },
      yaxis: {
        title: {
          text: '단위 (원)',
        },
        labels: {
          formatter: (v: any) => v.toLocaleString('ko-KR'),
        },
      },
    }),
    [weeksArr, getWeekStartDate],
  )

  // 시리즈 생성 함수들 메모화
  const makeSeries = useCallback(
    (index: number, label: string, color: string) => {
      // 데이터 결정 로직을 더 명확하게 개선
      let nationalData

      if (
        effectiveAreaCd &&
        effectiveAreaCd !== '' &&
        effectiveAreaCd !== null
      ) {
        // 지역이 선택된 경우: 전국 데이터가 있으면 사용, 없으면 기본 데이터 사용
        if (safeNationalWeekData.length > 0) {
          nationalData =
            index === 0 ? safeNationalWeekData[0] : safeNationalWeekData[1]
        } else {
          nationalData =
            index === 0 ? safeWeekGraphData[0] : safeWeekGraphData[1]
        }
      } else {
        // 전국 선택인 경우: 기본 데이터 사용
        nationalData = index === 0 ? safeWeekGraphData[0] : safeWeekGraphData[1]
      }

      return [
        {
          name: `전국 ${label}`,
          data: nationalData || [],
          type: 'bar',
          color,
        },
        ...(effectiveAreaNm &&
        effectiveAreaCd &&
        effectiveAreaCd !== '' &&
        effectiveAreaCd !== null &&
        safeWeekRegionGraphData.length > 0
          ? [
              {
                name: `${effectiveAreaNm} ${label}`,
                data: regionWeekGraphData[index],
                type: 'line',
                color: '#F59E0B',
                dashArray: 5,
              },
            ]
          : []),
        ...(effectiveBizNm
          ? [
              {
                name: `${effectiveBizNm} ${label}`,
                data: enhancedWeekData[index],
                type: 'line',
                color: index === 0 ? '#8B5CF6' : '#14B8A6',
              },
            ]
          : []),
      ]
    },
    [
      effectiveAreaCd,
      effectiveAreaNm,
      effectiveBizNm,
      safeNationalWeekData,
      safeWeekGraphData,
      safeWeekRegionGraphData,
      regionWeekGraphData,
      enhancedWeekData,
    ],
  )

  const makeMemberSeries = useCallback(
    (index: number, label: string, color: string) => {
      const nationalData =
        effectiveAreaCd &&
        effectiveAreaCd !== '' &&
        effectiveAreaCd !== null &&
        safeNationalWeekData.length > 0
          ? index === 2
            ? safeNationalWeekData[2]
            : safeNationalWeekData[3]
          : index === 2
            ? safeWeekGraphData[2]
            : safeWeekGraphData[3]

      return [
        {
          name: `전국 ${label}`,
          data: nationalData || [],
          type: 'bar',
          color,
        },
        ...(effectiveAreaNm &&
        effectiveAreaCd &&
        effectiveAreaCd !== '' &&
        effectiveAreaCd !== null &&
        safeWeekRegionGraphData.length > 0
          ? [
              {
                name: `${effectiveAreaNm} ${label}`,
                data: regionWeekGraphData[index],
                type: 'line',
                color: '#3B82F6',
                dashArray: 5,
              },
            ]
          : []),
        ...(effectiveBizNm
          ? [
              {
                name: `${effectiveBizNm} ${label}`,
                data: enhancedWeekData[index],
                type: 'line',
                color: index === 2 ? '#8B5CF6' : '#14B8A6',
              },
            ]
          : []),
      ]
    },
    [
      effectiveAreaCd,
      effectiveAreaNm,
      effectiveBizNm,
      safeNationalWeekData,
      safeWeekGraphData,
      safeWeekRegionGraphData,
      regionWeekGraphData,
      enhancedWeekData,
    ],
  )

  const makeTitle = useCallback(
    (label: string) => {
      // 1. 골프장명이 있으면 골프장명 사용
      if (effectiveBizNm) {
        return `${effectiveBizNm} ${label}`
      }

      // 2. 지역 데이터가 실제로 있고, 지역명이 유효한 경우에만 지역명 사용
      if (
        safeWeekRegionGraphData.length > 0 &&
        effectiveAreaNm &&
        effectiveAreaCd
      ) {
        return `${effectiveAreaNm} ${label}`
      }

      // 3. 그 외의 경우는 모두 전국
      return `전국 ${label}`
    },
    [effectiveBizNm, effectiveAreaNm, effectiveAreaCd, safeWeekRegionGraphData],
  )

  // holeType 표시명 생성 메모화
  const holeTypeLabel = useMemo(() => {
    return effectiveHoleType && getComCodeValue('HOLE_TYPE', effectiveHoleType)
      ? getComCodeValue('HOLE_TYPE', effectiveHoleType)?.codeNm + ' '
      : ''
  }, [effectiveHoleType, getComCodeValue])

  // 로딩 중일 때는 이전 상태를 유지하여 깜박임 방지
  if (
    !isInitialized ||
    (isNationalDataLoading && safeNationalWeekData.length === 0)
  ) {
    return (
      <GraphWrapper>
        <div>데이터를 불러오는 중...</div>
      </GraphWrapper>
    )
  }

  return (
    <GraphWrapper>
      <ChartContainer isMobile={isMobile}>
        {!isWeekNonMemberDataEmpty && (
          <GraphRow>
            <ApexBarChart
              key={`nonmember-weekday-${effectiveAreaCd}-${effectiveBizCd}-${chartRefreshKey}`}
              options={{
                ...commonOptions,
                title: {
                  text: makeTitle(`${holeTypeLabel}비회원 주중 평균 그린피`),
                  align: 'left',
                  style: { fontSize: '16px', fontWeight: 'bold' },
                },
              }}
              series={makeSeries(0, `${holeTypeLabel}비회원(주중)`, '#004488')}
              height="350px"
              width="100%"
            />

            <ApexBarChart
              key={`nonmember-weekend-${effectiveAreaCd}-${effectiveBizCd}-${chartRefreshKey}`}
              options={{
                ...commonOptions,
                title: {
                  text: makeTitle(`${holeTypeLabel}비회원 주말 평균 그린피`),
                  align: 'left',
                  style: { fontSize: '16px', fontWeight: 'bold' },
                },
              }}
              series={makeSeries(1, `${holeTypeLabel}비회원(주말)`, '#66CCFF')}
              height="350px"
              width="100%"
            />
          </GraphRow>
        )}

        {!isWeekMemberDataEmpty && mshipYn != 'N' && (
          <GraphRow>
            <ApexBarChart
              key={`member-weekday-${effectiveAreaCd}-${effectiveBizCd}-${chartRefreshKey}`}
              options={{
                ...commonOptions,
                title: {
                  text: makeTitle(`${holeTypeLabel}회원 주중 평균 그린피`),
                  align: 'left',
                  style: { fontSize: '16px', fontWeight: 'bold' },
                },
              }}
              series={makeMemberSeries(
                2,
                `${holeTypeLabel}회원(주중)`,
                '#B91C1C',
              )}
              height="350px"
              width="100%"
            />
            <ApexBarChart
              key={`member-weekend-${effectiveAreaCd}-${effectiveBizCd}-${chartRefreshKey}`}
              options={{
                ...commonOptions,
                title: {
                  text: makeTitle(`${holeTypeLabel}회원 주말 평균 그린피`),
                  align: 'left',
                  style: { fontSize: '16px', fontWeight: 'bold' },
                },
              }}
              series={makeMemberSeries(
                3,
                `${holeTypeLabel}회원(주말)`,
                '#F87171',
              )}
              height="350px"
              width="100%"
            />
          </GraphRow>
        )}
      </ChartContainer>
    </GraphWrapper>
  )
}

const GraphWrapper = styled.div`
  width: 100%;
  border: 1px solid #ddd;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 15px;
`

const GraphRow = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 30px;

  > div {
    flex: 1;
  }
`
const ChartContainer = styled.div<{ isMobile: boolean }>`
  ${({ isMobile }) =>
    isMobile
      ? `
        overflow-x: auto;
        overflow-y: hidden;
        width: 100%;
        padding:15px;
        
        &::-webkit-scrollbar {
          height: 10px;
        }
        
        &::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }
        
        &::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 3px;
        }
        
        &::-webkit-scrollbar-thumb:hover {
          background: #a1a1a1;
        }
      `
      : `
        width: 100%;
      `}
`
